import { CloseCircleFilled } from "@ant-design/icons";
import {
  SubscriptionCancellationReason,
  SubscriptionPlanType,
  SubscriptionScheduledChangeType,
  SubscriptionUpdateType,
} from "@coeff/api";
import { Input, Select, message } from "antd";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components";

import { Button, FlexColumn, Modal, Typography } from "../../../components";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import { BContext } from "../../../types";
import { useTrack } from "../../../utils";
import { planNameFromType } from "../../../utils/billingHelpers";
import { ableToSelfCancel } from "../../../utils/selfServeCancellationEligibility";
import { BillingContext } from "../Billing";

const { TextArea } = Input;

const REASON_OPTIONS: { value: SubscriptionCancellationReason; label: string }[] = [
  { value: SubscriptionCancellationReason.NoLongerNeed, label: "I no longer need Coefficient" },
  { value: SubscriptionCancellationReason.TooExpensive, label: "It's too expensive" },
  { value: SubscriptionCancellationReason.MissingFeatures, label: "It's missing features I need" },
  {
    value: SubscriptionCancellationReason.UsingAnotherTool,
    label: "I'm using another tool instead",
  },
  {
    value: SubscriptionCancellationReason.AiToolsReplacedCoefficient,
    label: "AI tooling replaced Coefficient",
  },
  { value: SubscriptionCancellationReason.Other, label: "Other" },
];

/** Step 1: what cancellation means + feature loss. Step 2: churn survey + API. */
type Step = "impact" | "survey";

type Props = {
  visible: boolean;
  onClose: () => void;
};

type UpdateSubResponse = { is_success?: boolean; error_msg?: string };

const featureLossItems = [
  "No auto data refresh",
  "5,000 rows per import (50 refreshes / month)",
  "100 rows per export (50 runs / month)",
  "No shared connections",
];

const BodyBlock = styled.div`
  color: ${COLORS.black65};
  font-size: 14px;
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
`;

const FeatureRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 8px;
  flex-wrap: wrap;
`;

/** Red border on the textarea when validation fails (antd 4 + showCount wraps in .ant-input-textarea). */
const DetailsFieldWrap = styled.div<{ $error: boolean }>`
  .ant-input-textarea textarea.ant-input {
    ${({ $error }) =>
      $error
        ? `
      border-color: ${COLORS.red} !important;
      &:hover {
        border-color: ${COLORS.red} !important;
      }
      &:focus {
        border-color: ${COLORS.red} !important;
      }
    `
        : ""}
  }
`;

export const SelfServeCancelModal: React.FC<Props> = ({ visible, onClose }) => {
  const {
    userSubscription,
    updateSubscription,
    syncBilling,
    currentUserEmail,
    basePath,
    isManager,
  } = useContext(BillingContext) as BContext;

  const history = useHistory();
  const track = useTrack();

  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const [step, setStep] = useState<Step>("impact");
  const [reason, setReason] = useState<SubscriptionCancellationReason | undefined>();
  const [details, setDetails] = useState("");
  const [detailsFieldError, setDetailsFieldError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [undoingCancel, setUndoingCancel] = useState(false);

  const subscriptionDetails = userSubscription?.subscription_details;

  const renewalOnDate = subscriptionDetails?.next_billing_cycle_dt ? (
    <>
      {" "}
      on{" "}
      <strong style={{ color: COLORS.black85 }}>
        {moment(subscriptionDetails.next_billing_cycle_dt).format("MMM DD, YYYY")}
      </strong>
    </>
  ) : null;

  const planLabel = planNameFromType(subscriptionDetails?.plan_type);

  const isAlreadyScheduledForCancel =
    Boolean(subscriptionDetails?.has_scheduled_changes) &&
    subscriptionDetails?.scheduled_change_type ===
      SubscriptionScheduledChangeType.CancelSubscription;

  useEffect(() => {
    if (visible) {
      track("billing_cancel_modal_opened", {
        current_plan_type: subscriptionDetails?.plan_type,
        has_scheduled_cancel: isAlreadyScheduledForCancel,
      });
    } else {
      setStep("impact");
      setReason(undefined);
      setDetails("");
      setDetailsFieldError(false);
    }
  }, [visible]);

  const handleClose = useCallback(() => {
    if (!submitting && !undoingCancel) {
      onClose();
    }
  }, [onClose, submitting, undoingCancel]);

  const handleUndoCancellation = useCallback(async () => {
    if (!subscriptionDetails?.subscription_id) {
      return;
    }
    setUndoingCancel(true);
    try {
      const res = (await updateSubscription({
        update_type: SubscriptionUpdateType.UndoCancellation,
        subscription_id: subscriptionDetails.subscription_id,
      })) as UpdateSubResponse;

      if (res?.is_success) {
        track("billing_cancel_undo_completed", {
          current_plan_type: subscriptionDetails?.plan_type,
          event_from: "cancel_modal",
        });
        await syncBilling();
        onClose();
        message.success("Cancellation has been undone.");
      } else {
        message.error(res?.error_msg ?? "Something went wrong.");
      }
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setUndoingCancel(false);
    }
  }, [subscriptionDetails, updateSubscription, syncBilling, onClose, track]);

  const handleSubmit = useCallback(async () => {
    if (!reason) {
      message.error("Please select why you're cancelling.");
      return;
    }
    const trimmed = details.trim();
    if (reason === SubscriptionCancellationReason.Other && !trimmed) {
      setDetailsFieldError(true);
      return;
    }
    setDetailsFieldError(false);

    if (!subscriptionDetails?.subscription_id) {
      message.error("Missing subscription. Please refresh and try again.");
      return;
    }

    if (
      !ableToSelfCancel({
        subscriptionDetails,
        clientFlags,
        userEmail: currentUserEmail,
        featureEnabled: true,
      })
    ) {
      message.error(
        "Self-serve cancellation isn't available for this account. Please contact support."
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = (await updateSubscription({
        update_type: SubscriptionUpdateType.CancelSubscription,
        subscription_id: subscriptionDetails.subscription_id,
        cancellation_reason: reason,
        cancellation_details: trimmed || undefined,
      })) as UpdateSubResponse;

      if (res?.is_success) {
        track("billing_cancel_completed", {
          current_plan_type: subscriptionDetails?.plan_type,
          cancellation_reason: reason,
          has_cancellation_details: Boolean(trimmed),
        });
        await syncBilling();
        onClose();
        history.push({
          pathname: `${basePath}/subscription`,
          search: window.location.search,
        });
      } else {
        message.error(res?.error_msg ?? "Something went wrong.");
      }
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setSubmitting(false);
    }
  }, [
    reason,
    details,
    subscriptionDetails,
    clientFlags,
    currentUserEmail,
    updateSubscription,
    syncBilling,
    onClose,
    history,
    basePath,
    track,
  ]);

  if (!subscriptionDetails || !isManager) {
    return null;
  }

  const impactTitle = `Cancel your ${planLabel} plan`;
  const alreadyCanceledTitle = `Your ${planLabel} plan has been canceled`;

  if (isAlreadyScheduledForCancel) {
    return (
      <Modal
        visible={visible}
        onCancel={handleClose}
        title={alreadyCanceledTitle}
        width={560}
        centered={true}
      >
        <FlexColumn gap={0}>
          <BodyBlock>
            <p style={{ margin: 0 }}>
              Your {planLabel} plan will be canceled at the end of your current billing cycle
              {renewalOnDate} and you'll be downgraded to the free plan.
              <br />
              To undo it, click <strong>Undo cancellation</strong> below.
            </p>
          </BodyBlock>
          <FooterRow>
            <Button
              type="link"
              noPadding
              loading={undoingCancel}
              onClick={handleUndoCancellation}
              style={{ fontWeight: 600 }}
            >
              Undo cancellation
            </Button>
            <Button type="primary" onClick={handleClose} disabled={undoingCancel}>
              Got it
            </Button>
          </FooterRow>
        </FlexColumn>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} onCancel={handleClose} title={impactTitle} width={560} centered={true}>
      {step === "impact" ? (
        <FlexColumn gap={0}>
          <BodyBlock>
            <p style={{ margin: "0 0 16px" }}>
              Your {planLabel} plan will be canceled at the end of your current billing cycle
              {renewalOnDate} and you'll be downgraded to the free plan
            </p>
            <Typography
              variant="body2"
              fontWeight={600}
              style={{ color: COLORS.black65, marginBottom: 0 }}
            >
              Free plan limitations:
            </Typography>
            <FeatureList>
              {featureLossItems.map(text => (
                <FeatureRow key={text}>
                  <CloseCircleFilled
                    style={{ color: COLORS.red, fontSize: 16, marginTop: 3, flexShrink: 0 }}
                  />
                  <span style={{ color: COLORS.black65 }}>{text}</span>
                </FeatureRow>
              ))}
            </FeatureList>
          </BodyBlock>

          <FooterRow>
            <Button
              type="link"
              noPadding
              onClick={() => {
                track("billing_cancel_kept_plan", {
                  current_plan_type: subscriptionDetails?.plan_type,
                  cancel_step: "impact",
                });
                handleClose();
              }}
              style={{ fontWeight: 600 }}
            >
              Keep the {planLabel} plan
            </Button>
            <Button
              type="primary"
              onClick={() => {
                track("billing_cancel_proceeded_to_survey", {
                  current_plan_type: subscriptionDetails?.plan_type,
                });
                setStep("survey");
              }}
            >
              Yes, proceed to cancel
            </Button>
          </FooterRow>
        </FlexColumn>
      ) : (
        <FlexColumn gap={0}>
          <BodyBlock style={{ marginBottom: 20 }}>
            <p style={{ margin: "0 0 12px" }}>
              Your {planLabel} plan will be canceled at the end of your current billing cycle
              {renewalOnDate} and you'll be downgraded to the free plan
            </p>
            <p style={{ margin: 0 }}>
              If you need help with your plan,{" "}
              <a href="mailto:support@coefficient.io">contact support</a>.
              {subscriptionDetails?.plan_type !== SubscriptionPlanType.Starter && (
                <>
                  {" "}
                  You can also{" "}
                  <Link
                    to={{ pathname: `${basePath}/plans`, search: window.location.search }}
                    onClick={() => {
                      track("billing_cancel_downgrade_clicked", {
                        current_plan_type: subscriptionDetails?.plan_type,
                      });
                      onClose();
                    }}
                  >
                    downgrade your plan
                  </Link>{" "}
                  instead of canceling.
                </>
              )}
            </p>
          </BodyBlock>
          <div style={{ marginBottom: 16 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: 8, color: COLORS.black85 }}
            >
              Why are you cancelling? <span style={{ color: COLORS.red }}>*</span>
            </Typography>
            <Select<SubscriptionCancellationReason>
              style={{ width: "100%" }}
              placeholder="Select a reason"
              value={reason}
              onChange={v => {
                setReason(v);
                setDetailsFieldError(false);
              }}
              options={REASON_OPTIONS}
            />
          </div>
          <DetailsFieldWrap $error={detailsFieldError} style={{ marginBottom: 4 }}>
            <TextArea
              rows={4}
              value={details}
              onChange={e => {
                setDetails(e.target.value);
                setDetailsFieldError(false);
              }}
              placeholder="Share details..."
              maxLength={5000}
              showCount
              aria-invalid={detailsFieldError}
              aria-required={reason === SubscriptionCancellationReason.Other}
            />
          </DetailsFieldWrap>
          <FooterRow>
            <Button
              type="link"
              noPadding
              onClick={() => {
                track("billing_cancel_kept_plan", {
                  current_plan_type: subscriptionDetails?.plan_type,
                  cancel_step: "survey",
                });
                handleClose();
              }}
              disabled={submitting}
              style={{ fontWeight: 600 }}
            >
              Keep the {planLabel} plan
            </Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              Cancel plan
            </Button>
          </FooterRow>
        </FlexColumn>
      )}
    </Modal>
  );
};
